import { motion } from "framer-motion";
import {
  TIME_SLOTS,
  FLEX,
  ACCESS,
  AUCTION_FACILITIES,
} from "@/lib/quoteOptions";
import { TextInput, Select, RadioTiles, Checkbox, TextArea } from "@/components/quote/Fields";

export default function StepCollection({ data, update, onNext, onBack }) {
  const c = data.collection;
  const a = data.access;
  const au = data.auction;

  const setC = (k, val) => update({ collection: { ...c, [k]: val } });
  const setA = (k, val) => update({ access: { ...a, [k]: val } });
  const setAu = (k, val) => update({ auction: { ...au, [k]: val } });

  const today = new Date().toISOString().slice(0, 10);
  const canNext = !!c.date;

  return (
    <motion.div
      key="step-collection"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-8"
    >
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-3">
          Step 03 · Collection
        </div>
        <h1 className="font-display font-extrabold text-white text-3xl md:text-5xl leading-[1.05] tracking-tight">
          When and how?
        </h1>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <TextInput
          label="Collection date *"
          testId="quote-input-date"
          type="date"
          min={today}
          value={c.date}
          onChange={(e) => setC("date", e.target.value)}
        />
        <Select
          label="Preferred collection time"
          hint="Out-of-hours collections may carry an additional charge."
          testId="quote-input-time"
          value={c.time_slot}
          onChange={(e) => setC("time_slot", e.target.value)}
          options={TIME_SLOTS}
        />
      </div>

      <div className="border border-white/10 bg-[#0A0A0A] p-6 space-y-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold">
            Want the best available price?
          </div>
          <div className="text-zinc-400 text-sm mt-2">
            If your collection time is flexible, we may be able to match your
            vehicle with an existing route.
          </div>
        </div>
        <RadioTiles
          testId="quote-flexibility"
          value={c.flexibility}
          onChange={(v) => setC("flexibility", v)}
          options={FLEX}
          columns={2}
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <RadioTiles
            label="Access at collection"
            hint="Transporters need safe space to load and unload."
            testId="quote-access-pickup"
            value={a.collection_access}
            onChange={(v) => setA("collection_access", v)}
            options={ACCESS}
            columns={3}
          />
        </div>
        <div>
          <RadioTiles
            label="Access at delivery"
            testId="quote-access-dropoff"
            value={a.delivery_access}
            onChange={(v) => setA("delivery_access", v)}
            options={ACCESS}
            columns={3}
          />
        </div>
      </div>

      <div className="border border-white/10 bg-[#0A0A0A] p-6 space-y-4">
        <Checkbox
          id="auction"
          testId="quote-auction-toggle"
          label="Collection is from an auction, salvage yard or storage facility"
          checked={au.is_auction}
          onChange={(v) => setAu("is_auction", v)}
        />
        {au.is_auction && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="grid sm:grid-cols-2 gap-4 pt-2 overflow-hidden"
          >
            <Select
              label="Facility"
              testId="quote-auction-facility"
              value={au.facility}
              onChange={(e) => setAu("facility", e.target.value)}
              options={AUCTION_FACILITIES}
            />
            <TextInput
              label="Release / collection reference"
              testId="quote-auction-ref"
              placeholder="e.g. 12345678"
              value={au.reference}
              onChange={(e) => setAu("reference", e.target.value)}
            />
            <div className="sm:col-span-2 text-xs text-zinc-500">
              Up to 30 minutes of reasonable waiting/loading time is included.
              Additional waiting charges may apply where delays exceed this.
            </div>
          </motion.div>
        )}
      </div>

      <TextArea
        label="Anything else we should know?"
        testId="quote-input-notes"
        placeholder="e.g. vehicle is lowered, keys with reception, narrow access, accident damage…"
        rows={4}
        value={data.notes}
        onChange={(e) => update({ notes: e.target.value })}
      />

      <div className="flex justify-between pt-2 gap-3">
        <button type="button" onClick={onBack} data-testid="quote-collection-back" className="btn-ghost">
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canNext}
          data-testid="quote-collection-next"
          className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
        >
          See my quote
        </button>
      </div>
    </motion.div>
  );
}
